import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, BookOpen, Calendar, User, Tag, FileText,
  ArrowRight, ChevronRight, Eye, Filter, Download, ScrollText
} from "lucide-react";
import { Input } from "@/components/ui/input";
import ExecutiveHero from "@/components/ExecutiveHero";

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Cinematic research hero — scholar at work with annotated documents and
// open notebooks. Communicates "Research. Evidence. Discovery. Knowledge."
// within the first three seconds (per Visual Experience Upgrade Directive v1.0).
const JOURNAL_HERO = "https://images.unsplash.com/photo-1532153975070-2e9ab71f1b14?auto=format&fit=crop&w=2400&q=85"; // scholarly research & writing

const CATEGORIES = [
  "WAIT Theory", "Workforce Intelligence", "AI & Workforce", "Credential Fatigue",
  "Leadership", "Organizational Intelligence", "Workforce Economics",
  "Research Reports", "Institutional Strategy", "Global Workforce Systems",
];

export default function SCFRJournal() {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({ total_publications: 0, total_views: 0, total_authors: 0 });

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: "50" });
        if (selectedCategory) params.set("category", selectedCategory);
        if (selectedYear) params.set("year", selectedYear);
        const pubsRes = await fetch(`${API_URL}/api/scfr/publications?${params}`);
        const pubsData = pubsRes.ok ? await pubsRes.json() : { publications: [] };
        setPublications(pubsData.publications || []);

        const sRes = await fetch(`${API_URL}/api/scfr/stats`);
        if (sRes.ok) setStats(await sRes.json());
      } catch {
        /* keep empty */
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [selectedCategory, selectedYear]);

  const filtered = publications.filter((p) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      p.title?.toLowerCase().includes(t) ||
      p.author?.toLowerCase().includes(t) ||
      p.abstract?.toLowerCase().includes(t) ||
      p.tags?.some((tag) => tag.toLowerCase().includes(t))
    );
  });
  const featured = filtered.filter((p) => p.is_featured).slice(0, 3);

  const fmt = (d) => new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  return (
    <div className="min-h-screen zodiac-page" data-testid="scfr-journal-page">
      <ExecutiveHero
        eyebrow="Society & Credential Fatigue Review"
        title={<>The Zodiac institutional</>}
        accentTitle="research journal."
        subtitle="Peer-reviewed publications on workforce intelligence, credential fatigue, AI integration, and institutional modernization. Cinematic HTML reading experience with downloadable PDF for every paper."
        image={JOURNAL_HERO}
        breadcrumb={[{ label: "Home", href: "/" }, { label: "SCFR-Journal" }]}
        actions={[
          { label: "Submit Research", href: "/contact", testId: "scfr-submit-cta" },
        ]}
        testId="scfr-hero"
      />

      {/* Stats strip */}
      <section className="bg-[#0F1E47] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-3 gap-8">
          {[
            { value: stats.total_publications, label: "Publications" },
            { value: stats.total_views, label: "Total Views" },
            { value: stats.total_authors, label: "Contributors" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="zodiac-heading text-3xl lg:text-4xl font-bold text-[#C8A157] tabular-nums">{s.value || 0}</div>
              <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Search + Filters */}
      <section className="bg-[#FBFAF7] border-b border-[#E5E7EB] py-8" data-testid="scfr-search">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by title, author, abstract, or tag…"
                className="pl-12 h-12 bg-white border-[#E5E7EB] text-[#0F1E47] placeholder:text-[#9CA3AF]"
                data-testid="scfr-search-input"
              />
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`h-12 px-5 rounded-md border inline-flex items-center gap-2 transition-colors ${
                showFilters ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "border-[#E5E7EB] text-[#0F1E47] bg-white hover:bg-[#F8FAFC]"
              }`}
            >
              <Filter className="w-4 h-4" /> Filters
            </button>
          </div>
          {showFilters && (
            <div className="mt-4 flex flex-wrap gap-3">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[#0F1E47] text-sm"
                data-testid="scfr-filter-category"
              >
                <option value="">All Categories</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="h-10 px-3 rounded-md border border-[#E5E7EB] bg-white text-[#0F1E47] text-sm"
                data-testid="scfr-filter-year"
              >
                <option value="">All Years</option>
                {[2026, 2025, 2024].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
              {(selectedCategory || selectedYear) && (
                <button
                  onClick={() => { setSelectedCategory(""); setSelectedYear(""); }}
                  className="h-10 px-3 text-sm text-[#991B1B] hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
          )}
          <div className="mt-4 text-sm text-[#6B7280]">
            {loading ? "Loading…" : `${filtered.length} publication${filtered.length === 1 ? "" : "s"} found`}
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8">
              <ScrollText className="w-5 h-5 text-[#C8A157]" />
              <h2 className="zodiac-heading text-2xl font-bold text-[#0F1E47]">Featured Research</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((pub) => (
                <motion.div
                  key={pub.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/scfr/${pub.slug}`} className="zodiac-hairline-card group block overflow-hidden h-full">
                    <div className="aspect-[16/10] bg-[#F4F6FB] overflow-hidden">
                      {pub.featured_image_url ? (
                        <img src={pub.featured_image_url} alt={pub.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-10 h-10 text-[#9CA3AF]" />
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold mb-2">{pub.category}</div>
                      <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-2 line-clamp-2 group-hover:text-[#991B1B] transition-colors">
                        {pub.title}
                      </h3>
                      <p className="text-sm text-[#6B7280] line-clamp-2 mb-3">{pub.abstract}</p>
                      <div className="flex items-center gap-3 text-xs text-[#6B7280]">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {pub.author?.split(",")[0]}</span>
                        <span>·</span>
                        <span>{fmt(pub.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All publications */}
      <section className="bg-[#FBFAF7] border-t border-[#E5E7EB] py-16" data-testid="scfr-all-publications">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <FileText className="w-5 h-5 text-[#1E3A8A]" />
            <h2 className="zodiac-heading text-2xl font-bold text-[#0F1E47]">All Publications</h2>
          </div>
          {loading ? (
            <div className="text-center py-16 text-[#6B7280]">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-[#E5E7EB]">
              <FileText className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
              <p className="text-[#475569]">No publications match your search.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filtered.map((pub) => (
                <Link
                  key={pub.slug}
                  to={`/scfr/${pub.slug}`}
                  className="zodiac-hairline-card group flex flex-col md:flex-row gap-5 p-5"
                  data-testid={`scfr-pub-${pub.slug}`}
                >
                  {pub.featured_image_url && (
                    <div className="md:w-56 aspect-[16/10] md:aspect-auto md:h-32 rounded-lg overflow-hidden flex-shrink-0 bg-[#F4F6FB]">
                      <img src={pub.featured_image_url} alt={pub.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold">{pub.category}</span>
                      {pub.is_featured && (
                        <span className="text-[10px] uppercase tracking-widest text-[#C8A157] font-semibold">· Featured</span>
                      )}
                    </div>
                    <h3 className="zodiac-heading text-lg font-bold text-[#0F1E47] mb-1 group-hover:text-[#991B1B] transition-colors line-clamp-2">
                      {pub.title}
                    </h3>
                    {pub.subtitle && <p className="text-sm text-[#475569] mb-2 line-clamp-1">{pub.subtitle}</p>}
                    <p className="text-sm text-[#6B7280] line-clamp-2 mb-3">{pub.abstract}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-[#6B7280]">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{pub.author}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmt(pub.created_at)}</span>
                      {pub.views !== undefined && <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{pub.views} views</span>}
                      {pub.pdf_url && <span className="flex items-center gap-1 text-[#991B1B]"><Download className="w-3 h-3" />PDF</span>}
                    </div>
                  </div>
                  <ChevronRight className="hidden md:block w-5 h-5 text-[#9CA3AF] group-hover:text-[#991B1B] group-hover:translate-x-1 transition-all flex-shrink-0 self-center" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Editorial Mission */}
      <section className="bg-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="zodiac-eyebrow mb-3 inline-flex items-center gap-3">
            <span className="w-8 h-px bg-[#C8A157]" />
            Editorial Authority
            <span className="w-8 h-px bg-[#C8A157]" />
          </div>
          <h2 className="zodiac-heading text-3xl sm:text-4xl font-bold text-[#0F1E47] mb-5 leading-tight">
            A premium institutional record of <span className="italic text-[#991B1B] font-normal">workforce intelligence science.</span>
          </h2>
          <p className="text-[#475569] leading-relaxed mb-8">
            SCFR is the flagship publication of the Zodiac Workforce Intelligence Institute.
            It convenes doctrinal scholarship, peer-reviewed research, and executive analysis
            on the structural transformation of workforce systems in the Human-AI era.
          </p>
          <Link to="/contact">
            <button className="h-12 px-6 rounded-md bg-[#0F1E47] text-white hover:bg-[#1E3A8A] font-semibold inline-flex items-center gap-2">
              Submit Research <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
